import { fetchWithAuth, BASE_BACKEND_URL } from '../auth'
import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Box, useMediaQuery } from '@mui/material'
import cookie from 'react-cookies'
import ErrorView from '../components/ErrorView'
import LoadingScreen from '../components/LoadingScreen'
import TabsMui from '../layout/TabsMui'
import { layoutMobileMediaQuery } from '../layout/layoutConstants'
import { getCurrentDate, getTimestampFromDateTime } from '../helpers/formatDate'
import { validateAccessRole } from '../helpers/userRole'
import { canManageSettlementApproval } from '../global_context/capabilities/settlementCapabilities'
import { formatMoney } from '../helpers/utils'
import { SETTLEMENT_DELIVERED_PRODUCTS_TABLE_HEADERS } from '../constants/settlementDetailsConstants'
import {
  BASE_SETTLEMENT_DETAIL_TABS,
  CANCELLED_SETTLEMENT_DETAIL_TAB,
  REFUND_PRODUCTS_SETTLEMENT_DETAIL_TAB,
  SETTLEMENT_DETAIL_TAB
} from './helpers/detailsConstants'
import {
  applyDepositMutationToSettlement,
  buildDeliveredProductsRows,
  mergeSavedDepositsIntoSettlement
} from './helpers/detailsHelpers'
import SettlementApprovalActions, {
  SettlementApprovalStatus
} from './settlementsdetails/SettlementApprovalActions'
import SettlementCancelledDocumentsTab from './settlementsdetails/SettlementCancelledDocumentsTab'
import SettlementDeliveredProductsTab from './settlementsdetails/SettlementDeliveredProductsTab'
import SettlementDetailsTab from './settlementsdetails/SettlementDetailsTab'
import SettlementDocumentsTab from './settlementsdetails/SettlementDocumentsTab'
import SettlementPaymentBreakdownTab from './settlementsdetails/SettlementPaymentBreakdownTab'
import SettlementRefundProductsTab from './settlementsdetails/SettlementRefundProductsTab'
import {
  UI_COLOR_DANGER_BORDER,
  UI_COLOR_DANGER_SOFT,
  UI_COLOR_DANGER_TEXT,
  UI_COLOR_SUCCESS_EMPHASIS,
  UI_COLOR_SUCCESS_EMPHASIS_BG,
  UI_COLOR_SUCCESS_EMPHASIS_TEXT
} from '../constants/uiColors'

const ACTION_ALERT_DURATION_MS = 5000

const buildActionAlert = (message, severity = 'success') => ({
  id: Date.now(),
  severity,
  message
})

function SettlementsDetails() {
  const [isFetching, setIsFetching] = useState(false)
  const [selectedTab, setSelectedTab] = useState(SETTLEMENT_DETAIL_TAB.DETAILS)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [bankOptions, setBankOptions] = useState([])
  const [actionLoading, setActionLoading] = useState(false)
  const [hasUnsavedDepositChanges, setHasUnsavedDepositChanges] = useState(false)
  const [discardDepositChangesToken, setDiscardDepositChangesToken] = useState(0)
  const [savedDeposits, setSavedDeposits] = useState([])
  const [actionAlert, setActionAlert] = useState(null)
  const isMobileLayout = useMediaQuery(layoutMobileMediaQuery)

  const canWriteSettlements = canManageSettlementApproval()

  const canSeeAnnulledDocumentsTab = validateAccessRole(
    cookie.load('user_role')?.see_annulled_charges_collection
  )?.can_read

  const tabs = useMemo(() => {
    const resolvedTabs = [...BASE_SETTLEMENT_DETAIL_TABS]

    if (canSeeAnnulledDocumentsTab) {
      resolvedTabs.push(CANCELLED_SETTLEMENT_DETAIL_TAB)
    }

    resolvedTabs.push(REFUND_PRODUCTS_SETTLEMENT_DETAIL_TAB)
    return resolvedTabs
  }, [canSeeAnnulledDocumentsTab])

  const settlementId = useMemo(() => {
    const urlSplit = window.location.href.split('/')
    return urlSplit[urlSplit.length - 1]
  }, [])

  const fetchDataFromServer = async () => {
    setIsFetching(true)
    try {
      const response = await fetchWithAuth(`${BASE_BACKEND_URL}/settlements/${settlementId}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      })

      if (response === undefined || response === null) {
        setData(null)
        setError('No se encontró la liquidación.')
        return
      }

      setData(response)
      setError(null)
    } catch (requestError) {
      console.error('Error fetching data:', requestError)
      setData(null)
      setError('No se encontró la liquidación')
    } finally {
      setIsFetching(false)
    }
  }

  const fetchBanksFromServer = async () => {
    try {
      const response = await fetchWithAuth(`${BASE_BACKEND_URL}/banks`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      })

      const resolvedBanks = Array.isArray(response)
        ? response
        : Array.isArray(response?.result)
          ? response.result
          : []

      setBankOptions(resolvedBanks)
    } catch (requestError) {
      console.error('Error fetching banks:', requestError)
      setBankOptions([])
    }
  }

  const postSettlementAction = async (endpoint, body = null, { rethrow = false } = {}) => {
    setActionLoading(true)
    try {
      await fetchWithAuth(`${BASE_BACKEND_URL}/settlements/${settlementId}/${endpoint}`, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      })
      await fetchDataFromServer()
      return true
    } catch (requestError) {
      console.error('Error sending settlement action:', requestError)
      alert('No se pudo completar la acción. Intente nuevamente.')
      if (rethrow) throw requestError
      return false
    } finally {
      setActionLoading(false)
    }
  }

  const saveDeposits = async (deposits) => {
    setActionLoading(true)
    try {
      await fetchWithAuth(`${BASE_BACKEND_URL}/settlements/${settlementId}/update_deposits`, {
        method: 'POST',
        body: JSON.stringify({ deposits }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      })

      setData((current) => mergeSavedDepositsIntoSettlement(current, deposits))
    } catch (requestError) {
      console.error('Error updating deposits:', requestError)
      throw requestError
    } finally {
      setActionLoading(false)
    }
  }

  const mutateDeposit = async (method, depositId, deposit = null) => {
    setActionLoading(true)
    try {
      const suffix = depositId ? `/deposits/${depositId}` : '/deposits'
      const response = await fetchWithAuth(`${BASE_BACKEND_URL}/settlements/${settlementId}${suffix}`, {
        method,
        body: deposit ? JSON.stringify({ deposit }) : undefined,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      })

      const savedDeposit = response?.deposit

      if (method === 'POST' && savedDeposit?.id == null) {
        await fetchDataFromServer()
        return savedDeposit
      }

      setData((current) =>
        applyDepositMutationToSettlement(
          current,
          method,
          depositId,
          savedDeposit || (deposit ? { ...deposit, id: depositId } : null)
        )
      )
      return savedDeposit
    } finally {
      setActionLoading(false)
    }
  }

  const renderApprovalActions = ({ showDownload = true } = {}) => (
    <SettlementApprovalActions
      settlement={data}
      canWrite={canWriteSettlements}
      canSeeAnnulledDocumentsTab={canSeeAnnulledDocumentsTab}
      hasUnsavedChanges={hasUnsavedDepositChanges}
      loading={actionLoading}
      onDiscardUnsavedChanges={() => setDiscardDepositChangesToken((current) => current + 1)}
      onApprove={async () => {
        const approved = await postSettlementAction('approve_and_send_to_erp', {
          deposits: savedDeposits
        })

        if (approved) {
          setActionAlert(
            buildActionAlert(`La liquidación ${data?.uid} ha sido aprobada exitosamente.`)
          )
        }
      }}
      showStatus={false}
      showDownload={showDownload}
      sx={{ justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}
    />
  )

  useEffect(() => {
    const loadInitialData = async () => {
      await fetchDataFromServer()
      await fetchBanksFromServer()
    }

    loadInitialData()
  }, [])

  useEffect(() => {
    if (!actionAlert) return undefined

    const dismissTimer = window.setTimeout(() => setActionAlert(null), ACTION_ALERT_DURATION_MS)

    return () => window.clearTimeout(dismissTimer)
  }, [actionAlert])

  const buildDeliveredProductsCsvFile = (deliveredProducts = buildDeliveredProductsRows(data)) => {
    let rows = []
    rows.push(['Reporte:', 'Detalles de productos entregados'])
    rows.push(['Supervisor:', cookie.load('user_name') || 'N/A'])
    rows.push(['Usuario:', data.user_full_name || 'N/A'])
    rows.push(['Fecha de creación:', getTimestampFromDateTime(data.collected_at) || 'N/A'])
    rows.push([''])

    rows = rows.concat([
      SETTLEMENT_DELIVERED_PRODUCTS_TABLE_HEADERS,
      ...deliveredProducts.map((product) => [
        product.productCode || 'N/A',
        product.barcode || 'N/A',
        product.productName || 'N/A',
        product.unitOfSale || 'N/A',
        product.quantity || 0
      ])
    ])

    return rows
  }

  const buildRefundProductsCsvFile = () => {
    let rows = []
    rows.push(['Reporte:', 'Detalles de productos rechazados'])
    rows.push(['Número de liquidación:', data.uid || 'N/A'])
    rows.push(['Código de usuario:', data.user_uid || 'N/A'])
    rows.push(['Usuario:', data.user_full_name || 'N/A'])
    rows.push(['Fecha de creación:', getTimestampFromDateTime(data.collected_at) || 'N/A'])
    rows.push([''])

    const refundsReceived = [
      '# DE FACTURA',
      'CÓDIGO DE PRODUCTO',
      'CÓDIGO DE BARRA',
      'NOMBRE DEL PRODUCTO',
      'MOTIVO DE RECHAZO',
      'UNIDAD DE VENTA',
      'CANTIDAD',
      'PRECIO',
      'TOTAL'
    ]

    let receivedDetails = [
      ['DETALLES DE PRODUCTOS RECHAZADOS ASOCIADOS A ESTA LIQUIDACIÓN:'],
      [''],
      refundsReceived
    ]

    ;(data.refunds || []).forEach((refund) => {
      let refundReceiptHeader = [
        `${refund.invoice_number} | ${refund.client_db_ref} - ${refund.client_description}`
      ]
      receivedDetails.push(refundReceiptHeader)
      ;(refund.refund_details || []).forEach((detail) => {
        let detailArrayReceived = [
          '',
          detail.product_db_ref || 'N/A',
          detail.product_barcode || 'N/A',
          detail.product_description || 'N/A',
          detail.motive?.replaceAll('  ', '') || refund.motive?.replaceAll('  ', '') || 'N/A',
          detail.unit_of_sale || 'N/A',
          detail.quantity || 'N/A',
          formatMoney(detail.unit_price || 0.0),
          formatMoney(detail.extended_price || 0.0)
        ]
        receivedDetails.push(detailArrayReceived)
      })
    })
    rows = rows.concat(receivedDetails)
    return rows
  }

  const renderSelectedTab = () => {
    switch (selectedTab) {
      case SETTLEMENT_DETAIL_TAB.DOCUMENTS:
        return (
          <SettlementDocumentsTab
            settlement={data}
            actions={renderApprovalActions()}
            canWrite={canWriteSettlements}
          />
        )
      case SETTLEMENT_DETAIL_TAB.PAYMENT_BREAKDOWN:
        return <SettlementPaymentBreakdownTab settlement={data} actions={renderApprovalActions()} />
      case SETTLEMENT_DETAIL_TAB.CANCELLED:
        return (
          <SettlementCancelledDocumentsTab settlement={data} actions={renderApprovalActions()} />
        )
      case SETTLEMENT_DETAIL_TAB.DELIVERED_PRODUCTS: {
        const deliveredProducts = buildDeliveredProductsRows(data)

        return (
          <SettlementDeliveredProductsTab
            settlement={data}
            rows={deliveredProducts}
            csvData={buildDeliveredProductsCsvFile(deliveredProducts)}
            filename={`Reporte_Liquidacion_Productos_Entregados_${data?.user_full_name}_${data?.business?.name || 'N/A'}_${getCurrentDate()}.csv`}
            actions={null}
          />
        )
      }
      case SETTLEMENT_DETAIL_TAB.REFUND_PRODUCTS:
        return (
          <SettlementRefundProductsTab
            settlement={data}
            refunds={data?.refunds || []}
            csvData={buildRefundProductsCsvFile()}
            filename={`DetallesDeProductosRechazados_${data?.user_full_name}_${data?.business?.name || 'N/A'}_${getCurrentDate()}.csv`}
            actions={null}
          />
        )
      case SETTLEMENT_DETAIL_TAB.DETAILS:
      default:
        return (
          <SettlementDetailsTab
            settlement={data}
            actions={renderApprovalActions()}
            bankOptions={bankOptions}
            discardChangesToken={discardDepositChangesToken}
            onDirtyChange={setHasUnsavedDepositChanges}
            onDepositsChange={setSavedDeposits}
            onCreateDeposit={(deposit) => mutateDeposit('POST', null, deposit)}
            onUpdateDeposit={(id, deposit) => mutateDeposit('PATCH', id, deposit)}
            onDeleteDeposit={(id) => mutateDeposit('DELETE', id)}
            onNotify={(message, severity) => setActionAlert(buildActionAlert(message, severity))}
            depositsSaving={actionLoading}
            canWrite={canWriteSettlements}
          />
        )
    }
  }

  if (isFetching) {
    return <LoadingScreen text="Cargando..." />
  }

  if (error) {
    return <ErrorView error={error} />
  }

  if (!data) {
    return <LoadingScreen text="Cargando..." />
  }

  const actionAlertColors =
    actionAlert?.severity === 'error'
      ? {
          border: UI_COLOR_DANGER_BORDER,
          background: UI_COLOR_DANGER_SOFT,
          foreground: UI_COLOR_DANGER_TEXT
        }
      : {
          border: UI_COLOR_SUCCESS_EMPHASIS,
          background: UI_COLOR_SUCCESS_EMPHASIS_BG,
          foreground: UI_COLOR_SUCCESS_EMPHASIS_TEXT
        }

  return (
    <>
      <div
        className="main-show-header"
        style={{
          position: 'relative',
          marginBottom: isMobileLayout ? (actionAlert ? 16 : 24) : 40
        }}
      >
        <h2
          style={{
            float: 'none',
            margin: 0,
            height: 'auto',
            ...(isMobileLayout
              ? {
                  maxWidth: '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              : {})
          }}
        >
          Liquidación: {data?.uid}
        </h2>
        {actionAlert && (
          <Alert
            key={actionAlert.id}
            severity={actionAlert.severity || 'success'}
            onClose={() => setActionAlert(null)}
            sx={{
              position: isMobileLayout ? 'relative' : 'absolute',
              right: isMobileLayout ? undefined : 0,
              top: isMobileLayout ? undefined : '50%',
              transform: isMobileLayout ? undefined : 'translateY(-50%)',
              zIndex: 1,
              overflow: 'hidden',
              width: isMobileLayout ? '100%' : 'fit-content',
              maxWidth: '100%',
              minHeight: isMobileLayout ? 36 : 40,
              mx: 0,
              mt: isMobileLayout ? 1.25 : 0,
              mb: 0,
              px: isMobileLayout ? 1.5 : 2,
              py: isMobileLayout ? 1 : 1.25,
              border: `1px solid ${actionAlertColors.border}`,
              borderRadius: '4px',
              backgroundColor: actionAlertColors.background,
              color: actionAlertColors.foreground,
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              '@keyframes actionAlertProgress': {
                from: { transform: 'scaleX(1)' },
                to: { transform: 'scaleX(0)' }
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                bottom: 0,
                width: '100%',
                height: 3,
                backgroundColor: actionAlertColors.border,
                transformOrigin: 'left center',
                animation: `actionAlertProgress ${ACTION_ALERT_DURATION_MS}ms linear forwards`,
                pointerEvents: 'none'
              },
              '& .MuiAlert-icon': {
                color: actionAlertColors.foreground,
                fontSize: isMobileLayout ? 20 : 22,
                p: 0,
                mr: isMobileLayout ? 1 : 1.25,
                opacity: 1
              },
              '& .MuiAlert-message': {
                flex: '1 1 auto',
                py: 0,
                pr: isMobileLayout ? 1 : 1.5,
                fontSize: isMobileLayout ? 13 : 15,
                fontWeight: 500,
                lineHeight: 1.35,
                overflowWrap: 'anywhere',
                whiteSpace: 'pre-line'
              },
              '& .MuiAlert-action': {
                alignItems: 'center',
                color: actionAlertColors.foreground,
                m: 0,
                p: 0,
                pl: 0.5,
                ml: 0
              },
              '& .MuiIconButton-root': {
                p: 0.25,
                color: actionAlertColors.foreground
              },
              '& .MuiSvgIcon-root': {
                fontSize: isMobileLayout ? 18 : 20
              }
            }}
          >
            {actionAlert.message}
          </Alert>
        )}
      </div>

      <div className="index-table">
        <div className="show-area">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              borderBottom: 1,
              borderColor: 'divider',
              mb: 2
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TabsMui
                tabs={tabs}
                activeTab={selectedTab}
                onChange={setSelectedTab}
                scrollable
                showDivider={false}
                marginBottom={0}
              />
            </Box>

            <SettlementApprovalStatus settlement={data} sx={{ mb: { xs: 1, md: 0 } }} />
          </Box>

          {renderSelectedTab()}
        </div>
      </div>
    </>
  )
}

export default SettlementsDetails
